import React, { useRef, useReducer, useState } from "react";
import { Wizard, WizardPage, IWizardCommonProps } from "cayo.ui.wizards";
import { useIntl } from "react-intl";
import { AccessEntra, SelectUserAccount } from "../shared";
import { communicationChannelsWizardMessages } from "../shared/messages";
import { teamsWizardMessages } from "./messages";
import wizardStateReducer, { InitialState } from "./store";
import { logger } from "cayo.ui";
import {
  getConsentUrl,
  exchangeAuthCode,
  promotePermissions,
  getSigninConsentUrl,
  configureTeamsConnector,
} from "../shared/api";
import { openAuthWindowAndGetCode } from "../shared/helpers";
import { GrantStateType, ConfigTeams } from "../shared/types";
import { endpoints } from "@cayo/shared/services/endpoints.service";

type ActionType = (data: {
  credentialType?: string;
  appUrl: string;
  accountName: string;
}) => Promise<any>;

type WizardProps = IWizardCommonProps & {
  objects: { [key: string]: any }[];
};

export const TeamsCredentials = (props: WizardProps) => {
  const intl = useIntl();
  const wizardRef = useRef<{ resetError: () => void }>(null);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, InitialState);
  const [processing, setProcessing] = useState(false);
  const stateRef = useRef(InitialState);
  stateRef.current = wizardState;
  const objectId: string = props?.objects[0]?.id;

  const changedCredential = isFinished
    ? wizardState?.userAccount?.use
      ? wizardState?.userAccount?.name
      : wizardState?.adminAccount?.name
    : "";

  const handleChangeField = (form: string, data: any) => {
    dispatchWizardState({
      type: "update",
      payload: {
        form,
        data,
      },
    });
  };

  const handleGrant = (
    accountName: string,
    form: string,
    action: ActionType,
    callback?: (token: string) => void
  ) => {
    setProcessing(true);
    action({
      credentialType: "communicationChannel",
      appUrl: endpoints.redirectUri,
      accountName,
    }).then((res) => {
      if (res?.url) {
        openAuthWindowAndGetCode(res.url)
          .then(({ code, error, error_description }) => {
            if (code) {
              exchangeAuthCode({
                serviceType: "communicationChannel",
                authorizationCode: code,
                accountName,
              }).then((res) => {
                if (res?.value) {
                  setProcessing(false);
                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form,
                      data: {
                        grantState: "Granted",
                        token: res?.value,
                        error: "",
                      },
                    },
                  });

                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form: "grantedAccounts",
                      data: {
                        [form === "adminAccount" ? "admin" : "user"]: accountName,
                      },
                    },
                  });

                  callback && callback(res?.value);
                } else if (res?.message) {
                  setProcessing(false);
                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form,
                      data: {
                        error: res.message,
                      },
                    },
                  });
                }
              });
            } else if (!code && error) {
              setProcessing(false);
              dispatchWizardState({
                type: "update",
                payload: {
                  form,
                  data: {
                    grantState: "Invalid Credentials",
                    error: error_description || "",
                  },
                },
              });
            }
          })
          .catch(() => setProcessing(false));
      } else if (res?.message) {
        setProcessing(false);
        dispatchWizardState({
          type: "update",
          payload: {
            form,
            data: {
              error: res.message,
            },
          },
        });
      }
    });
  };

  return (
    <Wizard
      title={intl.formatMessage(teamsWizardMessages.changeCredTitle)}
      isFinished={isFinished}
      activePage={0}
      ref={wizardRef}
      showStepError
      closeConfirmationMessage={" "}
      {...props}
    >
      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.accessEntra)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.accessEntra)}
        subTitle=""
        isInputValid={
          wizardState?.adminAccount?.grantState === GrantStateType.granted &&
          wizardState?.adminAccount?.name === wizardState?.grantedAccounts?.admin
        }
      >
        <AccessEntra
          error={wizardState?.adminAccount?.error}
          accountName={wizardState?.adminAccount?.name}
          grantIsActive={!!wizardState?.adminAccount?.name}
          processing={processing}
          accountMissMatch={
            wizardState?.adminAccount?.grantState === GrantStateType.granted &&
            wizardState?.adminAccount?.name !== wizardState?.grantedAccounts?.admin
          }
          onGrant={() =>
            handleGrant(wizardState?.adminAccount?.name, "adminAccount", getConsentUrl, (token) => {
              promotePermissions({
                refreshToken: token,
                accountName: wizardState?.adminAccount?.name,
                credentialType: "communicationChannel",
              }).catch((err) => logger.log(err));
            })
          }
          grantState={wizardState?.adminAccount?.grantState}
          handleChange={(key, value) =>
            handleChangeField("adminAccount", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => {
          let data: ConfigTeams = {
            adminAccountName: stateRef.current?.adminAccount?.name,
            adminAccountToken: stateRef.current?.adminAccount?.token,
          };

          if (stateRef.current?.userAccount?.use) {
            data = {
              ...data,
              accountName: stateRef.current?.userAccount?.name,
              accountToken: stateRef.current?.userAccount?.token,
            };
          }

          return configureTeamsConnector(objectId, data, () => setIsFinished(true));
        }}
        title={intl.formatMessage(communicationChannelsWizardMessages.selectUserAccount)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.selectUserAccount)}
        subTitle=""
        isInputValid={
          (wizardState?.userAccount?.grantState === GrantStateType.granted &&
            wizardState?.userAccount?.name === wizardState?.grantedAccounts?.user) ||
          !wizardState?.userAccount?.use
        }
      >
        <SelectUserAccount
          error={wizardState?.userAccount?.error}
          accountName={wizardState?.userAccount?.name}
          grantIsActive={!!wizardState?.userAccount?.name}
          processing={processing}
          accountMissMatch={
            wizardState?.userAccount?.use &&
            wizardState?.userAccount?.grantState === GrantStateType.granted &&
            wizardState?.userAccount?.name !== wizardState?.grantedAccounts?.user
          }
          onGrant={() =>
            handleGrant(wizardState?.userAccount?.name, "userAccount", getSigninConsentUrl)
          }
          grantState={wizardState?.userAccount?.grantState}
          handleChange={(key, value) =>
            handleChangeField("userAccount", {
              [key]: value,
            })
          }
          resetError={() => {
            dispatchWizardState({
              type: "update",
              payload: {
                form: "userAccount",
                data: {
                  error: "",
                },
              },
            });
          }}
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        subTitle=""
      >
        <div>
          <b>{intl.formatMessage(teamsWizardMessages.changeCredential)}:</b> {changedCredential}
        </div>
      </WizardPage>
    </Wizard>
  );
};
